/**
 * AI Agents Console client — thin wrapper around the Agents Console gateway.
 *
 * Set REACT_APP_AGENT_URL in your .env to point at a deployed gateway (e.g.
 * staging). REACT_APP_AI_AGENTS_URL is kept as a fallback for the older
 * local-only gateway (default http://127.0.0.1:7800) so existing local setups
 * keep working unchanged. The gateway hosts all four xnotify-aiagents and
 * exposes a uniform POST /api/run/<agent> endpoint that returns
 * { ok, result, stdout, … }.
 */

const BASE = (
  process.env.REACT_APP_AGENT_URL ||
  process.env.REACT_APP_AI_AGENTS_URL ||
  "http://127.0.0.1:7800"
).replace(/\/$/, "");

/**
 * Read the (optional) service token + customer-id from localStorage so each
 * page doesn't have to re-ask the user. Operators can set these via the
 * Agents Console at the same URL, OR we can wire a one-time prompt in App.
 */
const KEYS = {
  serviceToken: "xnotify-aiagents.serviceToken",
  mcpBase: "xnotify-aiagents.mcpBase"
};

export function setServiceToken(token) {
  try { localStorage.setItem(KEYS.serviceToken, token); } catch { /* ignore */ }
}
export function getServiceToken() {
  try { return localStorage.getItem(KEYS.serviceToken) || ""; } catch { return ""; }
}
export function setMcpBase(url) {
  try { localStorage.setItem(KEYS.mcpBase, url); } catch { /* ignore */ }
}
export function getMcpBase() {
  try {
    return localStorage.getItem(KEYS.mcpBase)
      || process.env.REACT_APP_MCP_BASE
      || "https://mcp.xnotifydemo.com";
  } catch { return "https://mcp.xnotifydemo.com"; }
}

/**
 * Resolve the active customer_id from the JWT in localStorage.
 *
 * Returns `null` when no valid JWT is present so callers can refuse to
 * run an agent rather than silently falling back to the master tenant.
 *
 * DA-BUG-0218 — the RRS-issued JWT puts the cid under `userdata.customer_id`,
 * NOT at the top level. The earlier `payload.customer_id` lookup always
 * returned `undefined` → fallback `return 1` (MATW) → every tenant's
 * "Run AI Agent" button generated cohorts against MATW's data regardless
 * of which tenant was logged in.
 */
export function getActiveCustomerId() {
  try {
    const t = localStorage.getItem("customer-portal");
    if (!t) return null;
    const payload = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const cid =
      payload?.userdata?.customer_id ??
      payload?.userdata?.customerId ??
      payload?.customer_id ??
      payload?.customerId ??
      payload?.cid ??
      null;
    return cid != null ? Number(cid) : null;
  } catch {
    return null;
  }
}

/**
 * Health check — returns { ok, agents, env, ... } so the UI can show
 * whether the console is reachable + which env keys are set on the gateway.
 */
export async function health() {
  const r = await fetch(`${BASE}/api/health`);
  if (!r.ok) throw new Error(`agents-console unreachable (${r.status})`);
  return r.json();
}

/**
 * Run an agent. `agentId` is one of:
 *   "predictive-segmentation"
 *   "copy-suggester"
 *   "campaign-performance-analyst"
 *   "compliance-reviewer"
 *
 * `fields` is the agent-specific payload (see each tab in the console).
 * The function injects mcp_base, customer_id, and service_token from local
 * config so callers only have to pass the agent-specific knobs.
 */
export async function runAgent(agentId, fields = {}) {
  const cid = getActiveCustomerId();
  if (cid == null) {
    throw new Error("Not logged in — cannot run AI agent. Please log in first.");
  }
  // `fields.business_goal` is forwarded for predictive-segmentation (the
  // gateway whitelists the goal strings). Other agents ignore it.
  const body = {
    mcp_base: getMcpBase(),
    customer_id: cid,
    ...fields
  };
  return runFetch(`${BASE}/api/run/${agentId}`, body);
}

/**
 * Pre-flight readiness probe — predictive-segmentation only. Runs the
 * agent in --probe-only mode (discovery + readiness report; no
 * candidate generation, no LLM). Used by the BusinessGoalModal to
 * show data quality BEFORE the operator commits to a goal.
 */
export async function probePredictiveSegmentation() {
  const cid = getActiveCustomerId();
  if (cid == null) throw new Error("Not logged in");
  return runFetch(`${BASE}/api/probe/predictive-segmentation`, {
    mcp_base: getMcpBase(),
    customer_id: cid,
    scopes: "mcp:asklytics-agent,mcp:catalog-reader,mcp:analyst-readonly",
  });
}

async function runFetch(url, body) {
  const svc = getServiceToken();
  if (svc) body.service_token = svc;

  const r = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`agents-console ${url}: ${r.status} ${text.slice(0, 200)}`);
  }
  return r.json();
}

export const AGENTS_CONSOLE_URL = BASE;

// ─── Campaign AI Insights persistence (FEAT-0012) ───────────────────
// These hit RRS (NOT the agents-console) — the tab reads/writes
// persisted analysis from the master DB regardless of whether the
// agent has been run recently. Uses the same x-api-key + x-access-token
// the rest of the portal already sends.

function rrsHeaders() {
  return {
    "Content-Type": "application/json",
    "x-api-key":      (typeof localStorage !== "undefined" && localStorage.getItem("api-key"))      || "",
    "x-access-token": (typeof localStorage !== "undefined" && localStorage.getItem("customer-portal")) || "",
  };
}

function rrsBase() {
  return (process.env.REACT_APP_API_URL || "http://localhost:4025").replace(/\/$/, "");
}

export async function getLatestCampaignInsights(campaignId) {
  const r = await fetch(`${rrsBase()}/campaigns/${encodeURIComponent(campaignId)}/ai-insights/latest`, {
    method: "GET", headers: rrsHeaders(),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`HTTP ${r.status} — ${text.slice(0, 200)}`);
  }
  return r.json();
}

export async function listCampaignInsightsHistory(campaignId, limit = 20) {
  const r = await fetch(`${rrsBase()}/campaigns/${encodeURIComponent(campaignId)}/ai-insights?limit=${limit}`, {
    method: "GET", headers: rrsHeaders(),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

/**
 * Voice profile (FEAT-0008) — tenant-scoped brand voice. Used by the
 * Copy Suggester to decide between "Setup Brand Voice for AI" and
 * "Generate Templates (AI)" on the button surface.
 */
export async function getVoiceProfileSchema() {
  const r = await fetch(`${BASE}/api/voice-profile/schema`);
  if (!r.ok) throw new Error(`schema fetch failed (${r.status})`);
  return r.json();
}

export async function getVoiceProfile() {
  const cid = getActiveCustomerId();
  if (cid == null) return { ok: true, profile: null };
  const r = await fetch(`${BASE}/api/voice-profile/${cid}`);
  if (!r.ok) throw new Error(`voice-profile fetch failed (${r.status})`);
  return r.json();
}

export async function saveVoiceProfile(payload) {
  const cid = getActiveCustomerId();
  if (cid == null) throw new Error("Not logged in — cannot save voice profile.");
  const r = await fetch(`${BASE}/api/voice-profile/${cid}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`voice-profile save failed (${r.status}): ${text.slice(0, 200)}`);
  }
  return r.json();
}
